import { memo, useEffect, useState } from 'react'
import { ClockIcon, SquareIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { AgentStatus, ChatMessageErrorKind } from '@hub/contracts'
import { i18n } from '@/i18n'
import { orpc } from '@/lib/orpc-client'
import type { ChatMessage } from '@/lib/collab/use-chat-collab'
import { MessageActions, type Reaction } from '@/components/assistant-ui/elements/message-actions'
import { HubBotAvatar, useHubBotPalette } from '@/components/hub-bot-avatar'
import { ttsAvailable, useTts } from '@/lib/use-tts'
import { ErrorState } from '@/components/assistant-ui/elements/error-state'
import { ThinkingIndicator } from '@/components/assistant-ui/elements/thinking-indicator'
import { mono } from '@/components/assistant-ui/elements/surfaces'
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard'
import { isSlowTurn } from '@/lib/chat/slow-turn'
import { cn } from '@/lib/utils'
import { AgentSteps } from './agent-steps'
import { ArtifactList } from './artifact-list'
import { MessageMarkdown } from './message-markdown'
import { messageCopyText } from './ui-blocks/lazy-blocks'
import { MessageUsageMenu } from './message-usage-menu'
import { toolMeta } from './tool-meta'
import { formatDayDate, formatTime } from './time'

function errorTitle(kind: ChatMessageErrorKind): string {
  return i18n.t(`chat:errors.${kind}`)
}

function errorDetail(kind: ChatMessageErrorKind, retryable: boolean, message?: string): string {
  const hint =
    kind === 'interrupted'
      ? i18n.t('chat:errors.interruptedHint')
      : i18n.t(retryable ? 'chat:errors.retryableHint' : 'chat:errors.nonRetryableHint')
  return message ? `${hint} (${message.slice(0, 160)})` : hint
}

/** Live label for the turn in progress. */
export function statusLabel(status: AgentStatus): string {
  switch (status.state) {
    case 'queued':
      return i18n.t('chat:status.queued')
    case 'reasoning':
      return i18n.t('chat:status.reasoning')
    case 'tool-calling':
      return status.toolName
        ? toolMeta(status.toolName).activeLabel
        : i18n.t('chat:status.toolCalling')
    case 'responding':
      return i18n.t('chat:status.responding')
    case 'thinking':
    default:
      return i18n.t('chat:status.thinking')
  }
}

/** Ticks once a second while the turn runs, so elapsed labels stay honest. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [active])
  return now
}

function elapsedLabel(sinceIso: string | undefined, active: boolean, now: number) {
  if (!sinceIso || !active) return undefined
  const s = Math.max(0, Math.round((now - Date.parse(sinceIso)) / 1000))
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`
}

const REACTIONS_KEY = 'hub-chat-reactions'
function readReactions(): Record<string, Reaction> {
  try {
    return JSON.parse(localStorage.getItem(REACTIONS_KEY) ?? '{}') as Record<string, Reaction>
  } catch {
    return {}
  }
}

/**
 * Thumbs up/down: the client's own state lives in localStorage (fast, per
 * device); every change is also recorded per user+message in D1 through
 * chat.reactToMessage for product feedback.
 */
function useReaction(
  projectId: string,
  threadId: string,
  messageId: string
): [Reaction, (r: Reaction) => void] {
  const [reaction, setReaction] = useState<Reaction>(() => readReactions()[messageId] ?? null)
  const update = (next: Reaction) => {
    setReaction(next)
    try {
      const all = readReactions()
      if (next) all[messageId] = next
      else delete all[messageId]
      localStorage.setItem(REACTIONS_KEY, JSON.stringify(all))
    } catch {
      /* ignore */
    }
    void orpc.chat
      .reactToMessage({ projectId, threadId, messageId, reaction: next })
      .catch((err) => console.warn('[chat] reaction not saved:', err))
  }
  return [reaction, update]
}

interface AssistantMessageProps {
  message: ChatMessage
  projectId: string
  /** Live status when this is the message currently being produced. */
  liveStatus?: AgentStatus
  /**
   * The thread's latest HUB reply carries the living avatar (idle life, and
   * the working glow while the agent runs); every earlier one shows a still,
   * dimmed picture — no timers per message (board zsy4, round 4).
   */
  latest?: boolean
  /** The agent is running in this thread — glows on the latest reply's avatar. */
  working?: boolean
  onRetry?: (messageId: string) => void
  retrying?: boolean
}

function AssistantMessageImpl({
  message,
  projectId,
  liveStatus,
  latest = false,
  working = false,
  onRetry,
  retrying,
}: AssistantMessageProps) {
  const { t } = useTranslation('chat')
  const palette = useHubBotPalette()
  const isLive = !!liveStatus && liveStatus.state !== 'idle' && liveStatus.state !== 'error'
  const streamingText = isLive && liveStatus?.state === 'responding'
  const { isCopied, copyToClipboard } = useCopyToClipboard()
  const [reaction, setReaction] = useReaction(projectId, message.threadId, message.id)
  const [showDetails, setShowDetails] = useState(false)
  const now = useNow(isLive)
  const elapsed = elapsedLabel(message.createdAt, isLive, now)

  // Read-aloud (the configured TTS provider via /voice/tts, cached server-side).
  const [ttsEnabled, setTtsEnabled] = useState(false)
  useEffect(() => {
    void ttsAvailable().then(setTtsEnabled)
  }, [])
  const tts = useTts({
    projectId,
    onError: (reason) =>
      toast.error(t(reason === 'credits_exhausted' ? 'credits.exhaustedTts' : 'errors.ttsFailed')),
  })

  const steps = message.steps ?? []
  const artifacts = message.artifacts ?? []
  const hasBody = message.content.trim().length > 0
  const retry = onRetry ? () => onRetry(message.id) : undefined
  const error = message.error
  // A user stopped the reply: not a failure — the partial text stays a normal
  // message with a quiet note, and the action bar (copy, read aloud) works.
  const stopped = error?.kind === 'stopped'
  const failed = !!error && !stopped

  // Show the indicator when nothing else conveys progress yet (no running
  // step visible, no streaming text).
  const lastStep = steps[steps.length - 1]
  const stepShowsProgress = lastStep?.status === 'running'
  const showIndicator = isLive && !streamingText && !stepShowsProgress

  // 45 s without a first token — or still reasoning — is slow, not stuck
  // (PLAN v2 phase 1). Say so next to the running indicator; Stop stays where
  // it is, in the composer.
  const slow = isSlowTurn({
    live: isLive,
    startedAt: message.createdAt,
    state: liveStatus?.state ?? 'idle',
    producedOutput: hasBody || steps.length > 0,
    now,
  })

  return (
    <div
      data-slot="assistant-message"
      data-message-id={message.id}
      className="group flex flex-col gap-1.5"
    >
      <div className="text-muted-foreground flex items-center gap-2 text-xs">
        <HubBotAvatar
          palette={palette}
          size={24}
          animate={latest}
          working={latest && working}
          state={latest ? undefined : 'neutral'}
          className={cn('-my-1', !latest && 'opacity-50')}
          aria-label={i18n.t('common:app.name')}
        />
        {message.createdAt && (
          <span className={cn(mono, 'text-foreground/40 tabular-nums')}>
            {formatTime(message.createdAt)}
          </span>
        )}
        {message.completedAt && message.createdAt && (
          <span className={cn(mono, 'text-foreground/30 tabular-nums')}>
            ·{' '}
            {Math.max(
              1,
              Math.round((Date.parse(message.completedAt) - Date.parse(message.createdAt)) / 1000)
            )}
            s
          </span>
        )}
      </div>

      <AgentSteps steps={steps} active={isLive} onRetry={retry} retrying={retrying} />

      {hasBody && (
        <div className="bg-muted text-foreground rounded-2xl px-4 py-3">
          <MessageMarkdown
            content={message.content}
            projectId={projectId}
            citations={message.citations}
            streaming={isLive}
          />
        </div>
      )}

      {showIndicator && (
        <ThinkingIndicator label={statusLabel(liveStatus!)} elapsed={elapsed} className="py-1" />
      )}

      {slow && (
        <div
          data-slot="assistant-slow"
          role="status"
          className={cn(mono, 'text-foreground/45 flex items-center gap-1.5 text-[11px]')}
        >
          <ClockIcon className="size-2.5" aria-hidden />
          {t('status.takingLonger')}
        </div>
      )}

      <ArtifactList artifacts={artifacts} projectId={projectId} />

      {stopped && !isLive && (
        <div
          data-slot="assistant-stopped"
          className={cn(mono, 'text-foreground/45 flex items-center gap-1.5 text-[11px]')}
        >
          <SquareIcon className="size-2.5 fill-current" aria-hidden />
          {t(hasBody || steps.length > 0 ? 'errors.stopped' : 'errors.stoppedEmpty')}
        </div>
      )}

      {failed && !isLive && (
        <ErrorState
          title={errorTitle(error.kind)}
          detail={errorDetail(error.kind, error.retryable, error.message)}
          retrying={!!retrying}
          onRetry={retry ?? (() => {})}
          className={cn('max-w-none', !error.retryable && '[&_button]:hidden')}
        />
      )}

      {/* Failed turns get the error card's retry instead of the action bar. */}
      {!isLive && hasBody && !failed && (
        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 has-[[data-state=open]]:opacity-100 [@media(hover:none)]:opacity-100">
          <MessageActions
            tts={
              ttsEnabled
                ? { state: tts.state, onToggle: () => void tts.toggle(message.content) }
                : undefined
            }
            copied={isCopied}
            reaction={reaction}
            onCopy={() => {
              const text = messageCopyText(message.content)
              if (typeof text === 'string') copyToClipboard(text)
              else void text.then(copyToClipboard)
            }}
            onReactionChange={setReaction}
            onMore={() => {}}
            // The element's own "more" button is replaced by the usage dropdown.
            className="[&>button:last-child]:hidden"
          />
          <MessageUsageMenu
            usage={message.usage}
            content={message.content}
            createdAt={message.createdAt}
            completedAt={message.completedAt}
            toolCalls={steps.filter((s) => s.kind === 'tool').length}
            onToggleDetails={() => setShowDetails((v) => !v)}
          />
          {message.createdAt && (
            <span className={cn(mono, 'text-foreground/35 ms-1 tabular-nums')}>
              {formatDayDate(message.createdAt)}
            </span>
          )}
        </div>
      )}

      {showDetails && (
        <pre className="bg-foreground/[0.04] text-foreground/60 max-h-64 overflow-auto rounded-xl p-3 font-mono text-[11px] leading-snug whitespace-pre-wrap">
          {JSON.stringify(
            {
              id: message.id,
              createdAt: message.createdAt,
              completedAt: message.completedAt,
              steps,
              artifacts,
              citations: message.citations,
              usage: message.usage,
              error,
            },
            null,
            2
          )}
        </pre>
      )}
    </div>
  )
}

export const AssistantMessage = memo(AssistantMessageImpl)
